import { FC, useState } from 'react';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  vendorDialogTitles,
  vendorDialogSubtitles,
  regularExpressions,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import { Vendor } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { vendorInitialValues } from 'src/utils/initialValues.utils';
import { vendorValidationSchema } from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import TextField from 'src/components/InputFields/TextField';
import MobileField from 'src/components/InputFields/MobileField';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';

const VendorActionMenuButton: FC<{
  vendor: Vendor;
  fetchVendors: () => void;
  deleteVendor: (vendorId: string) => void;
}> = ({ vendor, fetchVendors, deleteVendor }) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleEditFormSubmit = async () => {
    if (!selectedVendor) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_VENDOR, {
        id: vendor.id,
        email: formik.values.email,
        organizationName: formik.values.organizationName,
        address: formik.values.address,
        contactPersonName: formik.values.contactPersonName,
        contactNo: `+${formik.values.countryCode} ${formik.values.phoneNumber}`,
      });
      setEditDialogOpen(false);
      fetchVendors();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = () => {
    setSelectedVendor(vendor);

    const phoneMatch = vendor.contactNo?.match(regularExpressions.PHONE_REGEX);
    const countryCode = phoneMatch ? phoneMatch[1] : '';
    const phoneNumber = phoneMatch
      ? phoneMatch[2].replace(regularExpressions.WHITESPACE_REGEX, '')
      : '';

    formik.setValues({
      email: vendor.email,
      organizationName: vendor.organizationName,
      address: vendor.address || '',
      contactPersonName: vendor.contactPersonName || '',
      countryCode: countryCode,
      phoneNumber: phoneNumber,
    });

    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedVendor(null);
    formik.resetForm();
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    try {
      if (dialogType === actionButtons.DELETE) {
        if (vendor.id) {
          await deleteVendor(vendor.id);
        }
        fetchVendors();
      } else if (
        dialogType === actionButtons.ACTIVATE ||
        dialogType === actionButtons.DEACTIVATE
      ) {
        const response = await axiosTokenInstance.patch(
          apiUrls.VENDOR_ACTIVATION,
          {
            id: vendor.id,
            isActive: dialogType === actionButtons.ACTIVATE,
          }
        );

        showSnackbar(response.data.message, 'success');
        fetchVendors();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
    }
  };

  const formik = useFormik({
    initialValues: vendorInitialValues,
    validationSchema: vendorValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const getDialogHeight = () => {
    const fieldGroups = [
      ['organizationName'],
      ['address'],
      ['contactPersonName'],
      ['phoneNumber'],
      ['email'],
    ];
    return calculateDialogHeight(formik, fieldGroups) + 200;
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleEditDialogOpen}
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={vendorDialogTitles.EDIT}
            subtitle={vendorDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
            formBottomMargin="40px"
          >
            {selectedVendor && (
              <>
                <TextField
                  width="480px"
                  id="organizationName"
                  label="Organization"
                  formikProps={formik}
                />

                <TextArea
                  id="address"
                  label="Address"
                  formikProps={formik}
                  rows={3}
                />

                <TextField
                  width="480px"
                  id="contactPersonName"
                  label="Point of Contact"
                  formikProps={formik}
                />

                <MobileField
                  width="480px"
                  id="contactNo"
                  label="Contact Number"
                  formikProps={formik}
                />

                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  formikProps={formik}
                />
              </>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() =>
              handleDialogOpen(
                vendor.isActive
                  ? actionButtons.DEACTIVATE
                  : actionButtons.ACTIVATE
              )
            }
            iconClassName={
              vendor.isActive
                ? 'bx bx-minus-circle'
                : 'bx bx-radio-circle-marked'
            }
            text={
              vendor.isActive
                ? actionButtons.DEACTIVATE
                : actionButtons.ACTIVATE
            }
          />

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? vendorDialogTitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? vendorDialogTitles.ACTIVATE
            : vendorDialogTitles.DEACTIVATE
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? vendorDialogSubtitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? vendorDialogSubtitles.ACTIVATE
            : vendorDialogSubtitles.DEACTIVATE
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--success)'
            : 'var(--error)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--warning)'
            : 'var(--error)'
        }
      />
    </>
  );
};
export default VendorActionMenuButton;
